/**
 * Browser fetch helper with timeout, idempotent retry, and exponential backoff + jitter.
 *
 * Design mirrors api/_lib/safe-fetch.js (server) but uses the browser's native fetch API
 * instead of Node's http/https modules.
 *
 * Retry policy:
 *   - Only GET / HEAD requests are retried (idempotent by definition).
 *   - Retriable HTTP status codes: 429, 502, 503, 504.
 *   - Retriable network errors: any TypeError thrown by fetch (offline, TCP reset, etc.).
 *   - 4xx (except 429) are NOT retried — they indicate a client bug.
 *   - POST / PUT / PATCH / DELETE are never retried to avoid unintended duplicate submissions.
 *
 * @module safe-fetch-client
 */

const MAX_RETRIES = 3;
const RETRY_BACKOFF_MS = [200, 400, 800];
const RETRIABLE_STATUS = new Set([429, 502, 503, 504]);

function jitter() {
    return Math.floor(Math.random() * 100);
}

function sleep(ms) {
    return new Promise(r => setTimeout(r, ms));
}

function isIdempotent(method) {
    const m = (method || 'GET').toUpperCase();
    return m === 'GET' || m === 'HEAD';
}

/**
 * Fetch wrapper with timeout, retry, and backoff.
 *
 * @param {string} url
 * @param {object}  [options]
 * @param {string}  [options.method='GET']
 * @param {object}  [options.headers={}]
 * @param {string|null} [options.body=null]
 * @param {number}  [options.timeoutMs=8000]
 * @returns {Promise<Response>}
 */
export async function safeFetchClient(url, options = {}) {
    const { method = 'GET', headers = {}, body = null, timeoutMs = 8000 } = options;
    const canRetry = isIdempotent(method);
    const maxAttempts = canRetry ? MAX_RETRIES + 1 : 1;
    let lastError;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), timeoutMs);

        try {
            const res = await fetch(url, {
                method,
                headers,
                body: body ?? undefined,
                signal: ctrl.signal,
            });
            clearTimeout(timer);

            if (canRetry && RETRIABLE_STATUS.has(res.status)) {
                // Store the error but only retry if there are attempts left
                lastError = Object.assign(new Error(`Retriable status ${res.status}`), { status: res.status });
                if (attempt >= maxAttempts - 1) throw lastError;
                // else: fall through to backoff + next attempt
            } else {
                return res;
            }
        } catch (err) {
            clearTimeout(timer);
            // Re-throw errors we ourselves created (retriable status exhausted)
            if (err === lastError) throw err;
            lastError = err;

            // Only retry network/abort errors on idempotent requests
            if (!canRetry || attempt >= maxAttempts - 1) throw err;
        }

        await sleep(RETRY_BACKOFF_MS[attempt] + jitter());
    }

    throw lastError;
}
